import type { DensityTrendPoint } from "@/features/deployment-dashboard/widgets/charts/density-trend";
import type { PopulationTrendAnalyticsPoint } from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics";
import type {
  ThroughputLineOption,
  ThroughputTrendPoint,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend";

export const EXPORT_FORMAT_OPTIONS = [
  { value: "pdf", label: "PDF" },
  { value: "png", label: "PNG (ZIP)" },
  { value: "csv", label: "CSV" },
] as const;

export type ExportFormat = (typeof EXPORT_FORMAT_OPTIONS)[number]["value"];

export const EXPORT_CHART_OPTIONS = [
  { value: "population-trend", label: "Total Population Trend" },
  { value: "density-trend", label: "Density Trend" },
  { value: "throughput-trend", label: "Throughput Trend" },
] as const;

export type ExportChartId = (typeof EXPORT_CHART_OPTIONS)[number]["value"];

export const ALL_CHART_IDS: readonly ExportChartId[] = EXPORT_CHART_OPTIONS.map(
  (option) => option.value
);

export type ExportChartAvailability =
  "ready" | "loading" | "error" | "empty" | "not-configured" | "not-selected" | "not-rendered";

export interface AnalyticsExportTheme {
  reportBackground: string;
  surfaceBackground: string;
  primaryText: string;
  secondaryText: string;
  accent: string;
}

export interface AnalyticsExportThroughputLineMetadata {
  id: string;
  name: string;
  areaId: string;
  areaName: string;
  positiveDirectionLabel: string;
  negativeDirectionLabel: string;
}

export interface AnalyticsExportReportMetadata {
  dateRangeLabel: string;
  areaLabel: string;
  exportedAtLabel: string;
  filterStartIso: string;
  filterEndIso: string;
  exportedAtIso: string;
  /** Metadata for every throughput line currently selected on the chart, if any. */
  throughputLines?: readonly AnalyticsExportThroughputLineMetadata[];
}

/**
 * Which areas actually contributed to a Population Trend export, distinct from
 * `AnalyticsExportReportMetadata.areaLabel` (shared across all charts, and
 * unaware of Population-specific unconfigured areas). Structured-clone safe
 * for the export worker.
 */
export interface AnalyticsExportPopulationScope {
  requestedAreaIds: readonly string[];
  configuredAreaIds: readonly string[];
  unconfiguredAreas: readonly { area_id: string; area_name: string }[];
}

type ExportChartSnapshot<TChartId extends ExportChartId, TData> =
  | {
      id: TChartId;
      availability: "ready";
      data: readonly TData[];
      image?: Blob;
    }
  | {
      id: TChartId;
      availability: Exclude<ExportChartAvailability, "ready">;
    };

export type PopulationTrendExportChartSnapshot =
  | {
      id: "population-trend";
      availability: "ready";
      data: readonly PopulationTrendAnalyticsPoint[];
      /** Which requested areas actually contributed, for partial-scope exports. */
      populationScope: AnalyticsExportPopulationScope;
      image?: Blob;
    }
  | {
      id: "population-trend";
      availability: Exclude<ExportChartAvailability, "ready">;
    };

export type DensityTrendExportChartSnapshot = ExportChartSnapshot<
  "density-trend",
  DensityTrendPoint
>;

/** Per-line trend data captured for the throughput chart export, at the time of export. */
export interface AnalyticsExportThroughputLineSnapshot {
  line: ThroughputLineOption;
  points: readonly ThroughputTrendPoint[];
}

export type ThroughputTrendExportChartSnapshot =
  | {
      id: "throughput-trend";
      availability: "ready";
      lines: readonly AnalyticsExportThroughputLineSnapshot[];
      image?: Blob;
    }
  | {
      id: "throughput-trend";
      availability: Exclude<ExportChartAvailability, "ready">;
    };

export type AnalyticsExportChartSnapshot =
  | PopulationTrendExportChartSnapshot
  | DensityTrendExportChartSnapshot
  | ThroughputTrendExportChartSnapshot;

export interface AnalyticsExportWorkerRequest {
  format: ExportFormat;
  selectedCharts: readonly ExportChartId[];
  charts: readonly AnalyticsExportChartSnapshot[];
  metadata: AnalyticsExportReportMetadata;
  theme: AnalyticsExportTheme;
}

export type AnalyticsExportWorkerResult =
  | {
      type: "success";
      file: Blob;
    }
  | {
      type: "failure";
      message: string;
    };
